import { definedMap, hasOwnProperty } from "collection-utils";
import * as React from "react";
import { defined, isUndefinedish, panic } from "../Support";
import type { SubscriptionClient } from "./component-helpers";
import { asArrayValue, loadedDefinedMap, nonLoading, SubscriptionManager } from "./component-helpers";
import type { ArgumentSpec, DeserializationSpec } from "./computation-types";
import type {
    AppEnvironment,
    AppOther,
    Context,
    EnvironmentBoundEvaluable,
    ForceValueProducer,
    PotentialValue,
    RenderEnvironment,
    UIClass,
    ValueProducer,
    ValueProducers,
} from "./types";
import { asUI, UI } from "./types";

export interface ReactRenderer {
    ui?: UIClass;

    renderContent(
        values: PotentialValue[],
        context: Context,
        env: RenderEnvironment,
        valueProducers: ValueProducers,
        force: ForceValueProducer,
        ui: UI,
        fromData: boolean
    ): React.ReactNode;
}

const registeredReactRenderers = new Map<string, ReactRenderer | GlideFunctionalRenderer<UI>>();

export function registerReactRenderer(name: string, renderer: ReactRenderer | GlideFunctionalRenderer<UI>): void {
    registeredReactRenderers.set(name, renderer);
}

function lookupReactRenderer(name: string): ReactRenderer | GlideFunctionalRenderer<UI> {
    const renderer = registeredReactRenderers.get(name);
    if (renderer === undefined) {
        return panic(`No ReactRenderer for ${name}`);
    }
    return renderer;
}

type DirtyComponent = {
    forceUpdateFromData: () => void;
};
const dirtyComponents = new Set<DirtyComponent>();

export function markComponentDirty(component: DirtyComponent): void {
    const needTimeout = dirtyComponents.size === 0;
    dirtyComponents.add(component);
    if (!needTimeout) return;
    setTimeout(() => {
        const components = Array.from(dirtyComponents);
        dirtyComponents.clear();
        for (const c of components) {
            c.forceUpdateFromData();
        }
    });
}

export abstract class SubscribingReactComponent<TProps, TState>
    extends React.PureComponent<TProps, TState>
    implements SubscriptionClient<AppOther, AppEnvironment, React.ReactNode>
{
    public readonly _subscriptionManager = new SubscriptionManager(this);
    public fromData: boolean = false;

    constructor(props: TProps) {
        super(props);

        this.state = this.expandState(props, null);
    }

    public forceUpdateFromData() {
        this.fromData = true;
        this.forceUpdate();
    }

    // FIXME: This should go away, we just need to rewrite a few subclasses that
    // implement this.
    public abstract expandState(props: TProps, oldState: TState | null): TState;

    public debugPrint(..._items: any[]): void {
        return;
    }

    public componentWillUnmount(): void {
        this._subscriptionManager.unsubscribe();
    }

    public setDirty(): void {
        markComponentDirty(this);
    }

    public abstract recompute(force: ForceValueProducer): React.ReactNode;

    public render(): React.ReactNode {
        return this._subscriptionManager.run(true);
    }
}

export abstract class SubscribingReactUnpureComponent<TProps, TState>
    extends React.Component<TProps, TState>
    implements SubscriptionClient<AppOther, AppEnvironment, React.ReactNode>
{
    private readonly _subscriptionManager = new SubscriptionManager(this);
    protected fromData: boolean = false;

    constructor(props: TProps) {
        super(props);

        this.state = this.expandState(props, null);
    }

    public forceUpdateFromData() {
        this.fromData = true;
        this.forceUpdate();
    }

    // FIXME: This should go away, we just need to rewrite a few subclasses that
    // implement this.
    protected abstract expandState(props: TProps, oldState: TState | null): TState;

    protected debugPrint(..._items: any[]): void {
        return;
    }

    public componentWillUnmount(): void {
        this._subscriptionManager.unsubscribe();
    }

    public setDirty(): void {
        markComponentDirty(this);
    }

    public abstract recompute(force: ForceValueProducer): React.ReactNode;

    public render(): React.ReactNode {
        return this._subscriptionManager.run(true);
    }
}

interface GlideReactComponentProps {
    ui: ReactUI;
    renderer: ReactRenderer;
    context: Context;
    renv: RenderEnvironment;
    id: number;
}

class GlideReactComponent extends SubscribingReactComponent<GlideReactComponentProps, {}> {
    private _lastRenderedUI: ReactUI | undefined;
    private _lastRenderedContext: Context | undefined;
    private _valueProducers: ValueProducers | undefined;

    // protected debugPrint(...items: any[]): void {
    //     if (!(this.props.renderer instanceof GridMaterialRenderer)) return;

    //     logInfo(...items);
    // }

    public expandState(): {} {
        return {};
    }

    public recompute(force: ForceValueProducer): React.ReactNode {
        const { ui, context, renv, renderer } = this.props;
        if (
            this._valueProducers === undefined ||
            this._lastRenderedUI !== ui ||
            this._lastRenderedContext !== context
        ) {
            if (this._valueProducers === undefined) {
                this.debugPrint("new value producers because no old value producers", ui);
            }
            if (this._lastRenderedUI !== ui) {
                this.debugPrint("new value producers because different ui", ui, this._lastRenderedUI);
            }
            if (this._lastRenderedContext !== context) {
                this.debugPrint(
                    "new value producers because different context",
                    ui,
                    context,
                    this._lastRenderedContext
                );
            }
            this._lastRenderedUI = ui;
            this._lastRenderedContext = context;

            this._valueProducers = ui.getValueProducers(context);
        }

        // This can be done with .map and .definedMap but its in a prformance critical path
        // and the call overhead actually manages to account for a non-trivial amount of time
        // when rapidly scrolling.
        // FIXME: Really???
        const pv: PotentialValue[] = [];
        for (const vp of this._valueProducers) {
            if (vp === undefined) {
                pv.push(undefined);
            } else {
                pv.push(force(vp));
            }
        }

        const { fromData } = this;
        this.fromData = false;

        return renderer.renderContent(
            pv,
            context,
            renv,
            this._valueProducers,
            force,
            ui,
            fromData || renv.flags.needsRerender
        );
    }
}

export interface GlideFunctionalRendererProps<T extends UI = UI> {
    // valueProducers: ValueProducers;
    ctx: Context;
    env: RenderEnvironment;
    getValueProducers: (ctx: Context) => ValueProducers;
    ui: T;
}
export type GlideFunctionalRenderer<T extends UI = UI> = React.FunctionComponent<GlideFunctionalRendererProps<T>>;

function isFunctionalRenderer(
    renderer: ReactRenderer | GlideFunctionalRenderer<UI>
): renderer is GlideFunctionalRenderer<UI> {
    return typeof renderer === "function";
}

export abstract class ReactUI extends UI {
    protected readonly args: (EnvironmentBoundEvaluable | undefined)[];

    constructor(private readonly _componentName: string, ...args: (EnvironmentBoundEvaluable | undefined)[]) {
        super();
        this.args = args;
    }

    public readonly getValueProducers = (context: Context): ValueProducers => {
        return this.args.map(a => definedMap(a, x => x.eval(context)));
    };

    public render(context: Context, renv: RenderEnvironment, key?: string | number): React.ReactElement {
        const renderer = lookupReactRenderer(this._componentName);

        if (isFunctionalRenderer(renderer)) {
            const Renderer = renderer;
            return <Renderer ctx={context} env={renv} getValueProducers={this.getValueProducers} ui={this} />;
        } else {
            return (
                <GlideReactComponent
                    key={key}
                    id={Math.floor(Math.random() * 100000)}
                    ui={this}
                    renderer={renderer}
                    context={context}
                    renv={renv}
                />
            );
        }
    }
}

export class EmptyUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["Empty", "ui"];
    }
}

export function renderEmptyAsUndefined(ui: UI, ctx: Context, env: RenderEnvironment): React.ReactNode {
    const rendered = definedMap(ui, t => t.render(ctx, env));
    if (!isUndefinedish(rendered) && typeof rendered === "object" && hasOwnProperty(rendered, "props")) {
        if (rendered.props.ui instanceof EmptyUI) {
            return undefined;
        }
    }
    return rendered;
}

export function renderValueProducer(
    maybeVP: ValueProducer | undefined,
    force: ForceValueProducer,
    ctx: Context,
    env: RenderEnvironment,
    emptyAsUndefined: boolean
): React.ReactNode {
    return definedMap(maybeVP, vp =>
        loadedDefinedMap(force(vp), t =>
            emptyAsUndefined ? renderEmptyAsUndefined(asUI(t), ctx, env) : asUI(t).render(ctx, env)
        )
    );
}

export class LabelUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["Label", "ui", ["text", "evaluable"]];
    }
}

export class CheckboxAccessoryUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "CheckboxAccessory",
            "ui",
            ["value", "evaluable"],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
        ];
    }
}

export class CircleButtonAccessoryUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["CircleButtonAccessory", "ui", ["icon", "evaluable"], ["onClick", "action", false]];
    }
}

export class ImageAccessoryUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["ImageAccessory", "ui", ["icon", "evaluable"], ["onClick", "action", false]];
    }
}

export class HoldContainerUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["HoldContainer", "ui", ["child", "ui"]];
    }
}

export class ColumnUI extends ReactUI {
    public readonly getValueProducers = (context: Context): ValueProducers => {
        const [itemsEvaluable, styleEvaluable] = this.args;
        const items = defined(itemsEvaluable).eval(context);
        const style = definedMap(styleEvaluable, s => s.eval(context));
        return [items, style, ...asArrayValue(nonLoading(items.current))];
    };

    static get spec(): DeserializationSpec<"ui"> {
        return ["Column", "ui", ["children", "evaluable"], ["style", "evaluable", false]];
    }
}

export class SpansUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["Spans", "ui", ["layout", "evaluable"]];
    }
}

export class ClickableUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["Clickable", "ui", ["child", "ui"], ["onTap", "action"]];
    }
}

export class HighlightComponentUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["HighlightComponent", "ui", ["child", "ui"], ["componentID", "evaluable"]];
    }
}

export class ImageItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Image",
            "ui",
            ["image", "evaluable"],
            ["validFlag", "evaluable", false],
            ["size", "evaluable", false],
            ["sizing", "evaluable", false],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
            ["style", "evaluable", false],
            ["enlarge", "evaluable", false],
            ["onTap", "evaluable", false],
            ["showUploadButton", "evaluable", false],
            ["gravity", "evaluable", false],
            ["topLeftOverlay", "evaluable", false],
            ["topRightOverlay", "evaluable", false],
            ["bottomLeftOverlay", "evaluable", false],
            ["bottomRightOverlay", "evaluable", false],
        ];
    }
}

export class AudioPlayerUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["AudioPlayer", "ui", ["audio", "evaluable"]];
    }
}

export class TextBoxUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "TextBox",
            "ui",
            ["text", "evaluable"],
            ["style", "evaluable", false],
            ["justify", "evaluable", false],
            ["allCaps", "evaluable", false],
            ["allowWrapping", "evaluable", false],
            ["lineLimit", "evaluable", false],
        ];
    }
}

export class NoteUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Note",
            "ui",
            ["placeholder", "evaluable", false],
            ["text", "evaluable", false],
            ["onChange", "action", false],
            ["onTap", "action", false],
            ["isEnabled", "evaluable", false],
            ["title", "evaluable", false],
            ["isRequired", "evaluable", false],
        ];
    }
}

export class HintUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Hint",
            "ui",
            ["title", "evaluable", false],
            ["text", "evaluable", false],
            ["mood", "evaluable"],
            ["justify", "evaluable", false],
        ];
    }
}

export class OverlayEditorUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["OverlayEditor", "ui", ["value", "evaluable"], ["placeholder", "evaluable"]];
    }
}

export class SearchBarUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "SearchBar",
            "ui",
            ["placeholder", "evaluable"],
            ["text", "evaluable"],
            ["searchActive", "evaluable", false],
        ];
    }
}

export class SwitchUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "ToggleSwitch",
            "ui",
            ["value", "evaluable"],
            ["caption", "evaluable", false],
            ["description", "evaluable", false],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
            ["visual", "evaluable", false],
            ["allowWrapping", "evaluable", false],
        ];
    }
}

export class CheckboxUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Checkbox",
            "ui",
            ["value", "evaluable"],
            ["caption", "evaluable", false],
            ["description", "evaluable", false],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
        ];
    }
}

export class ChoiceUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Choice",
            "ui",
            ["value", "evaluable"],
            ["choices", "evaluable"],
            ["caption", "evaluable", false],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
            ["isRequired", "evaluable", false],
            ["style", "evaluable", false],
            ["isMulti", "evaluable", false],
            ["pushScreen", "action", false],
        ];
    }
}

export class ImagePickerUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "ImagePicker",
            "ui",
            ["value", "evaluable"],
            ["onChange", "evaluable", false],
            ["isEnabled", "evaluable", false],
            ["validFlag", "evaluable", false],
            ["caption", "evaluable", false],
            ["isRequired", "evaluable", false],
            ["cameraHint", "evaluable", false],
        ];
    }
}

export class FilePickerUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "FilePicker",
            "ui",
            ["value", "evaluable"],
            ["onChange", "evaluable", false],
            ["isEnabled", "evaluable", false],
            ["validFlag", "evaluable", false],
            ["caption", "evaluable", false],
            ["isRequired", "evaluable"],
        ];
    }
}

export class LikeUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["Like", "ui", ["value", "evaluable"], ["onChange", "action", false], ["caption", "evaluable", false]];
    }
}

export class ReactionUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Reaction",
            "ui",
            ["reactions", "evaluable"],
            ["value", "evaluable"],
            ["onChange", "action", false],
            ["title", "evaluable", false],
        ];
    }
}

export class RatingUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Rating",
            "ui",
            ["value", "evaluable"],
            ["onChange", "action", false],
            ["caption", "evaluable", false],
            ["maxRating", "evaluable"],
            ["ratingUnit", "evaluable"],
        ];
    }
}

export class MarkdownItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "MarkdownItem",
            "ui",
            ["markdown", "evaluable"],
            ["caption", "evaluable", false],
            ["onTap", "action", false],
        ];
    }
}

export class TagOverlayUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["TagOverlay", "ui", ["text", "evaluable"], ["corners", "evaluable"]];
    }
}

export class AvatarOverlayUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "AvatarOverlay",
            "ui",
            ["image", "evaluable", false],
            ["caption", "evaluable", false],
            ["textSize", "evaluable", false],
        ];
    }
}

export class ToggleIconOverlayUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["ToggleIconOverlay", "ui", ["value", "evaluable"], ["onChange", "action"]];
    }
}

export class InlineListUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["InlineList", "ui", ["list", "evaluable"], ["item", "ui"]];
    }
}

const listProperties: ArgumentSpec[] = [
    ["list", "evaluable"],
    ["item", "ui"],
    ["caption", "evaluable", false],
    ["emptyMessage", "evaluable", false],
    ["onSeeAll", "action", false],
    ["isGrouped", "evaluable", false],
    ["allowWrapping", "evaluable", false],
];

export class ListUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["List", "ui", ...listProperties];
    }
}

export class CheckListUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["CheckList", "ui", ...listProperties];
    }
}

export class SmallListUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["SmallList", "ui", ...listProperties];
    }
}

export class ListItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "ListItem",
            "ui",
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["image", "evaluable", false],
            ["icon", "evaluable", false],
            ["fallbackInitials", "evaluable", false],
            ["allowWrapping", "evaluable", false],
            ["onTap", "action", false],
            ["onDropImage", "action", false],
            ["actionSource", "evaluable", false],
            ["style", "evaluable", false],
        ];
    }
}

export class AdaptiveListItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "AdaptiveListItem",
            "ui",
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["caption", "evaluable", false],
            ["image", "evaluable", false],
            ["icon", "evaluable", false],
            ["fallbackInitials", "evaluable", false],
            ["onTap", "action", false],
            ["onDropImage", "action", false],
            ["actionSource", "evaluable", false],
            ["flags", "evaluable", false],
            ["accessoryView", "ui", false],
            ["accessoryPosition", "evaluable", false],
            ["itemID", "evaluable", false],
        ];
    }
}

export class PhoneNumberUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "PhoneNumber",
            "ui",
            ["number", "evaluable"],
            ["caption", "evaluable", false],
            ["onPhoneCall", "action", false],
            ["onTextMessage", "action", false],
        ];
    }
}

export class GridUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["Grid", "ui", ["list", "evaluable"], ["item", "ui"]];
    }
}

export class GridItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "GridItem",
            "ui",
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["image", "evaluable", false],
            ["size", "evaluable", false],
            ["fallbackInitials", "evaluable", false],
            ["onTap", "action", false],
            ["onDropImage", "action", false],
        ];
    }
}

export class HorizontalTilesUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "HorizontalTiles",
            "ui",
            ["list", "evaluable"],
            ["item", "ui"],
            ["numColumns", "evaluable"],
            ["caption", "evaluable", false],
            ["onSeeAll", "action", false],
            ["isGrouped", "evaluable", false],
            ["emptyMessage", "evaluable", false],
        ];
    }
}

export class HorizontalTilesItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "HorizontalTilesItem",
            "ui",
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["image", "evaluable", false],
            ["size", "evaluable"],
            ["fallbackInitials", "evaluable", false],
            ["onTap", "action", false],
            ["onDropImage", "action", false],
            ["corners", "evaluable", false],
            ["textPosition", "evaluable", false],
            ["overlayTextAlignment", "evaluable", false],
            ["textSize", "evaluable", false],
            ["textStyle", "evaluable", false],
            ["gravity", "evaluable", false],
            ["itemID", "evaluable", false],
            ["topLeftOverlay", "evaluable", false],
            ["topRightOverlay", "evaluable", false],
            ["bottomLeftOverlay", "evaluable", false],
            ["bottomRightOverlay", "evaluable", false],
        ];
    }
}

export class VerticalTilesUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "VerticalTiles",
            "ui",
            ["numColumns", "evaluable"],
            ["padding", "evaluable", false],
            ["textPosition", "evaluable", false],
            ...listProperties,
        ];
    }
}

export class CardItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "CardItem",
            "ui",
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["header", "evaluable", false],
            ["image", "evaluable", false],
            ["size", "evaluable"],
            ["onTap", "action", false],
            ["onDropImage", "action", false],
            ["numTextLines", "evaluable"],
            ["corners", "evaluable"],
            ["floatStyle", "evaluable"],
            ["textSize", "evaluable"],
            ["textStyle", "evaluable"],
            ["isHorizontal", "evaluable"],
            ["itemID", "evaluable", false],
            ["topLeftOverlay", "evaluable", false],
            ["topRightOverlay", "evaluable", false],
            ["bottomLeftOverlay", "evaluable", false],
            ["bottomRightOverlay", "evaluable", false],
        ];
    }
}

export class VerticalTilesItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "VerticalTilesItem",
            "ui",
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["image", "evaluable", false],
            ["size", "evaluable"],
            ["padding", "evaluable"],
            ["fallbackInitials", "evaluable", false],
            ["onTap", "action", false],
            ["onDropImage", "action", false],
            ["allowWrapping", "evaluable", false],
            ["corners", "evaluable", false],
            ["textPosition", "evaluable", false],
            ["overlayTextAlignment", "evaluable", false],
            ["textSize", "evaluable", false],
            ["textStyle", "evaluable", false],
            ["gravity", "evaluable", false],
            ["itemID", "evaluable", false],
            ["topLeftOverlay", "evaluable", false],
            ["topRightOverlay", "evaluable", false],
            ["bottomLeftOverlay", "evaluable", false],
            ["bottomRightOverlay", "evaluable", false],
        ];
    }
}

export class ColumnListUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["ColumnList", "ui", ["list", "evaluable"], ["item", "ui"]];
    }
}

export class ColumnItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "ColumnItem",
            "ui",
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["image", "evaluable", false],
            ["fallbackInitials", "evaluable", false],
            ["onTap", "action", false],
            ["onDropImage", "action", false],
        ];
    }
}

export class GalleryListUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["GalleryList", "ui", ["list", "evaluable"], ["item", "ui"], ["numColumns", "evaluable", false]];
    }
}

export class GalleryItemUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "GalleryItem",
            "ui",
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["image", "evaluable", false],
            ["fallbackInitials", "evaluable", false],
            ["onTap", "action", false],
            ["onDropImage", "action", false],
        ];
    }
}

export class CalendarListUI extends ReactUI {
    public state: any;

    static get spec(): DeserializationSpec<"ui"> {
        return [
            "CalendarList",
            "ui",
            ["list", "evaluable"],
            ["emptyMessage", "evaluable", false],
            ["caption", "evaluable", false],
            ["defaultMode", "evaluable", false],
            ["order", "evaluable", false],
            ["onTap", "action", false],
            ["insideInlineList", "evaluable", false],
            ["showTime", "evaluable", false], // defaults to true
        ];
    }
}

export class MapListUI extends ReactUI {
    public state: any;

    static get spec(): DeserializationSpec<"ui"> {
        return [
            "MapScreenList",
            "ui",
            ["list", "evaluable"],
            ["markers", "evaluable"],
            ["item", "ui"],
            ["searchActive", "evaluable", false],
            ["defaultMode", "evaluable", false],
            ["insideInlineList", "evaluable", false],
            ["allowUserLocation", "evaluable", false],
            ["emptyMessage", "evaluable", false],
            ["onTap", "action", false],
            ["visualType", "evaluable", false],
        ];
    }
}

export class TinderUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Tinder",
            "ui",
            ["list", "evaluable"],
            ["contents", "ui"],
            ["onLeft", "action"],
            ["onRight", "action"],
            ["emptyMessage", "evaluable", false],
            ["rowToSet", "evaluable", false],
        ];
    }
}

export class TitleUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "BigItem",
            "ui",
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["image", "evaluable", false],
            ["onTap", "action", false],
            ["onDropImage", "action", false],
            ["appearance", "evaluable", false],
            ["aspect", "evaluable", false],
        ];
    }
}

// New UI Here
export class UserProfileUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "UserProfile",
            "ui",
            ["image", "evaluable", false],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
            ["title", "evaluable", false],
            ["subtitle", "evaluable", false],
            ["removeBranding", "evaluable", false],
        ];
    }
}

export class MapUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Map",
            "ui",
            ["latitude", "evaluable", false],
            ["longitude", "evaluable", false],
            ["zoomLevel", "evaluable", false],
            ["onTap", "action", false],
            ["address", "evaluable", false],
        ];
    }
}

export class PieChartUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "PieChart",
            "ui",
            ["list", "evaluable"],
            ["title", "evaluable", false],
            ["caption", "evaluable", false],
            ["units", "evaluable", false],
            ["showTotal", "evaluable", false],
            ["total", "evaluable", false],
            ["showLegend", "evaluable", false],
            ["color", "evaluable", false],
            ["style", "evaluable", false],
        ];
    }
}

export class BarChartUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "BarChart",
            "ui",
            ["list", "evaluable"],
            ["title", "evaluable", false],
            ["caption", "evaluable", false],
            ["showLegend", "evaluable", false],
            ["showLabels", "evaluable", false],
            ["seriesLabels", "evaluable", false],
            ["color", "evaluable", false],
            ["style", "evaluable", false],
        ];
    }
}

export class StackedBarChartUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "StackedBarChart",
            "ui",
            ["list", "evaluable"],
            ["title", "evaluable", false],
            ["caption", "evaluable", false],
            ["showLegend", "evaluable", false],
            ["showLabels", "evaluable", false],
            ["color", "evaluable", false],
        ];
    }
}

export class YouTubeUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["YouTube", "ui", ["videoID", "evaluable"]];
    }
}

export class CommentsUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Comments",
            "ui",
            ["comments", "evaluable"],
            ["onPost", "action", false],
            ["onTapNew", "action", false],
            ["onDelete", "action", false],
            ["topic", "evaluable", false],
            ["userProfiles", "evaluable", false],
        ];
    }
}

export class ChatUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Chat",
            "ui",
            ["comments", "evaluable"],
            ["onPost", "action", false],
            ["onTapNew", "action", false],
            ["onDelete", "action", false],
            ["topic", "evaluable", false],
            ["userProfiles", "evaluable", false],
        ];
    }
}

export class ButtonUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Button",
            "ui",
            ["text", "evaluable"],
            ["appearance", "evaluable"],
            ["floatingIcon", "evaluable", false],
            ["onTap", "action"],
            ["confirm", "evaluable", false],
            ["confirmTitle", "evaluable", false],
            ["confirmDesc", "evaluable", false],
            ["confirmStyle", "evaluable", false],
            ["acceptText", "evaluable", false],
            ["cancelText", "evaluable", false],
            ["mood", "evaluable", false],
            ["nonBlocking", "evaluable", false],
        ];
    }
}

export class ButtonBarUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "ButtonBar",
            "ui",
            ["textLeft", "evaluable", false],
            ["appearanceLeft", "evaluable"],
            ["iconLeft", "evaluable", false],
            ["onTapLeft", "action"],

            ["textRight", "evaluable", false],
            ["appearanceRight", "evaluable"],
            ["iconRight", "evaluable", false],
            ["onTapRight", "action"],

            ["moodLeft", "evaluable", false],
            ["moodRight", "evaluable", false],
        ];
    }
}

export class PivotPickerUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["PivotPicker", "ui", ["pivotNames", "evaluable"], ["current", "evaluable"], ["onChange", "action"]];
    }
}

export class SimpleTableUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "SimpleTable",
            "ui",
            ["caption", "evaluable", false],
            ["footer", "evaluable", false],
            ["rows", "evaluable"],
            ["allowWrapping", "evaluable", false],
        ];
    }
}

class BaseFieldClass {
    public static makeSpec(
        fieldName: string,
        valueName: string,
        extraArgs?: ArgumentSpec[]
    ): DeserializationSpec<"ui"> {
        const args: ArgumentSpec[] = [
            ["placeholder", "evaluable"],
            [valueName, "evaluable"],
            ["title", "evaluable"],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
            ["isRequired", "evaluable", false],
            ["validFlag", "evaluable", false],
            ["minLength", "evaluable", false],
            ["maxLength", "evaluable", false],
            ...(extraArgs ?? []),
        ];
        return [fieldName, "ui", ...args];
    }
}

export class TextFieldUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return BaseFieldClass.makeSpec("TextField", "text", [["size", "evaluable", false]]);
    }
}

export class DateTimeFieldUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return BaseFieldClass.makeSpec("DateTimeField", "dateTime", [["pastAndOrFuture", "evaluable", false]]);
    }
}

export class DateFieldUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return BaseFieldClass.makeSpec("DateField", "date", [["pastAndOrFuture", "evaluable", false]]);
    }
}

export class NumberFieldUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return BaseFieldClass.makeSpec("NumberField", "number", [
            ["minValue", "evaluable", false],
            ["maxValue", "evaluable", false],
            ["prefix", "evaluable", false],
            ["suffix", "evaluable", false],
        ]);
    }
}

export class PhoneFieldUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return BaseFieldClass.makeSpec("PhoneField", "number");
    }
}

export class VideoUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["Video", "ui", ["url", "evaluable"]];
    }
}

export class SeparatorUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["Separator", "ui", ["spacing", "evaluable", false], ["showLine", "evaluable", false]];
    }
}

export class EmailAuthenticationUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["EmailAuthentication", "ui", ["onSuccess", "action"], ["onAbort", "action"]];
    }
}

export class BuyScreenUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "BuyScreen",
            "ui",
            // Old-style item (a single one)
            ["itemName", "evaluable", false],
            ["itemDescription", "evaluable", false],
            ["itemImage", "evaluable", false],
            ["itemSKU", "evaluable", false],
            ["itemPrice", "evaluable", false],
            ["currencyCode", "evaluable", false],
            ["withShipping", "evaluable", false],
            ["buttonID", "evaluable", false],
            // This must be given in both cases
            ["onFinished", "action"],
            // This is still part of the old-style item
            ["rowIndex", "evaluable", false],
            // New-style items (multiple)
            ["items", "evaluable", false],
            ["showStripeLogo", "evaluable", false],
        ];
    }
}

export class ProgressUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "ProgressIndicator",
            "ui",
            ["value", "evaluable"],
            ["minimum", "evaluable", false],
            ["maximum", "evaluable", false],
            ["title", "evaluable", false],
            ["caption", "evaluable", false],
            ["style", "evaluable", false],
            ["formatted", "evaluable", false],
        ];
    }
}

export class CircleProgressUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "CircleProgressIndicator",
            "ui",
            ["value", "evaluable"],
            ["minimum", "evaluable", false],
            ["maximum", "evaluable", false],
            ["title", "evaluable", false],
            ["caption", "evaluable", false],
            ["showTotal", "evaluable", false],
            ["style", "evaluable", false],
            ["formatted", "evaluable", false],
        ];
    }
}

export class EmailFieldUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return BaseFieldClass.makeSpec("EmailField", "email");
    }
}

export class WebviewUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Webview",
            "ui",
            ["link", "evaluable"],
            ["aspectRatio", "evaluable"],
            ["title", "evaluable"],
            ["allowScrolling", "evaluable"],
            ["explicitHeight", "evaluable", false],
        ];
    }
}

export class LocationEntryUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "LocationEntry",
            "ui",
            ["value", "evaluable"],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
            ["validFlag", "evaluable", false],
            ["isRequired", "evaluable", false],
        ];
    }
}

export class StopwatchUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Stopwatch",
            "ui",
            ["startTime", "evaluable"],
            ["duration", "evaluable"],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
        ];
    }
}

export class SignatureFieldUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "SignatureField",
            "ui",
            ["placeholder", "evaluable"],
            ["imageURL", "evaluable"],
            ["onTap", "action"],
            ["onChange", "action", false],
            ["isEnabled", "evaluable", false],
            ["title", "evaluable", false],
            ["isRequired", "evaluable", false],
            ["validFlag", "evaluable", false],
        ];
    }
}

export class SignaturePadUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "SignaturePad",
            "ui",
            ["message", "evaluable"],
            ["signatureURL", "evaluable"],
            ["onChange", "action", false],
            ["shouldWrite", "evaluable", false],
            ["validFlag", "evaluable", false],
            ["hasPaths", "evaluable", false],
            ["shouldWriteCV", "evaluable", false],
            ["isWritingCV", "evaluable", false],
        ];
    }
}

export class CodeScannerUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "CodeScanner",
            "ui",
            ["standards", "evaluable"],
            ["acceptOnFirst", "evaluable"],
            ["output", "evaluable"],
            ["onSuccess", "action", false],
            ["triggerSource", "evaluable", false],
        ];
    }
}

export class LiveLinearBarcodeScannerUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return ["LiveLinearBarcodeScanner", "ui", ["output", "evaluable"], ["onSuccess", "action", false]];
    }
}

export class EventPickerUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "EventPicker",
            "ui",
            ["selectionStart", "evaluable"],
            ["selectionEnd", "evaluable"],
            ["events", "evaluable", false],
        ];
    }
}

export class EventPickerButtonUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "EventPickerButton",
            "ui",
            ["isEnabled", "evaluable", false],
            ["eventStart", "evaluable"],
            ["eventEnd", "evaluable"],
            ["onClick", "action", false],
            ["onClear", "action", false],
            ["title", "evaluable", false],
            ["placeholder", "evaluable", false],
            ["isRequired", "evaluable", false],
        ];
    }
}

export class ShareScreenUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "ShareScreen",
            "ui",
            ["title", "evaluable"],
            ["author", "evaluable"],
            ["displayQRCode", "evaluable"],
            ["onFeedback", "action"],
            ["onReport", "action", false],
        ];
    }
}

export class WeatherUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "Weather",
            "ui",
            ["location", "evaluable", false],
            ["onTap", "action", false],
            ["hideCity", "evaluable", false],
        ];
    }
}

export class LocalTimeUI extends ReactUI {
    static get spec(): DeserializationSpec<"ui"> {
        return [
            "LocalTime",
            "ui",
            ["location", "evaluable", false],
            ["onTap", "action", false],
            ["hideCity", "evaluable", false],
        ];
    }
}
